import React, { useState } from 'react';
import { X, Loader2, Package, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = '';

const ALL_PRODUCTS = [
  'Yaki Straight', 'Silky Straight', 'Bone Straight',
  'Deep Wave', 'Loose Wave', 'Natural Wave', 'Water Wavy', 'Body Wave',
  'Deep Curl', 'Loose Curl', 'Kinky Curl', 'Afro Kinky',
];

const LENGTHS = ['8"', '10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"', '32"', '34"', '36"', '38"', '40"'];

const COLORS = [
  'Natural Black (1B)', 'Off Black (1)', 'Dark Brown (2)', 'Medium Brown (4)',
  'Chocolate Brown (4B)', 'Light Brown (6)', 'Chestnut Brown (8)', 'Medium Golden Brown (10)',
  'Honey Blonde (27)', 'Ash Blonde (18)', 'Platinum Blonde (613)', 'Strawberry Blonde (30)',
  'Dark Auburn (33)', 'Auburn (35)', 'Burgundy (99J)', 'Copper Red (350)',
  'Deep Red (530)', 'Dark Ginger (130)', 'Jet Black (1)', 'Silver Grey',
  'Ombre 1B/27', 'Ombre 1B/30', 'Ombre 1B/99J', 'Ombre 1B/613',
  'Piano P1B/27', 'Piano P1B/30', 'Balayage', 'Custom (Specify in Notes)',
];

const COUNTRIES = {
  'AT': 'Austria', 'BE': 'Belgium', 'BG': 'Bulgaria', 'HR': 'Croatia', 'CY': 'Cyprus',
  'CZ': 'Czech Republic', 'DK': 'Denmark', 'EE': 'Estonia', 'FI': 'Finland', 'FR': 'France',
  'DE': 'Germany', 'GR': 'Greece', 'HU': 'Hungary', 'IE': 'Ireland', 'IT': 'Italy',
  'LV': 'Latvia', 'LT': 'Lithuania', 'LU': 'Luxembourg', 'MT': 'Malta', 'NL': 'Netherlands',
  'PL': 'Poland', 'PT': 'Portugal', 'RO': 'Romania', 'SK': 'Slovakia', 'SI': 'Slovenia',
  'ES': 'Spain', 'SE': 'Sweden', 'GB': 'United Kingdom', 'US': 'United States', 'CA': 'Canada',
  'AU': 'Australia', 'BR': 'Brazil', 'JP': 'Japan', 'KR': 'South Korea', 'MX': 'Mexico',
  'NZ': 'New Zealand', 'NG': 'Nigeria', 'SA': 'Saudi Arabia', 'SG': 'Singapore',
  'ZA': 'South Africa', 'CH': 'Switzerland', 'AE': 'UAE', 'NO': 'Norway',
};

const BulkOrderForm = ({ isOpen, onClose, preSelectedProduct = '', preSelectedColors = [] }) => {
  const [form, setForm] = useState({
    product_name: preSelectedProduct,
    color: preSelectedColors.length > 0 ? preSelectedColors[0] : '',
    length: '',
    quantity: '',
    additional_notes: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    postal_code: '',
    country: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // Prevent double-submit
    if (parseInt(form.quantity) < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/bulk-order`, {
        ...form,
        quantity: parseInt(form.quantity),
        country: COUNTRIES[form.country] || form.country,
      });
      toast.success('Bulk order inquiry submitted! We\'ll contact you within 24-48 hours.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const colorOptions = preSelectedColors.length > 0 ? preSelectedColors : COLORS;
  const showProductSelect = !preSelectedProduct;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-[var(--bg-surface)] border border-[var(--text-primary)]/10 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="bulk-order-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[var(--bg-surface)] border-b border-[var(--text-primary)]/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5" style={{ color: 'var(--gold)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Bulk Order Inquiry</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--text-primary)]/5 rounded transition-colors" data-testid="close-bulk-order">
            <X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Product Selection (only for footer/standalone) */}
          {showProductSelect && (
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Product *</label>
              <select name="product_name" required value={form.product_name} onChange={handleChange}
                className="input-field w-full text-base sm:text-sm" data-testid="bulk-product-select">
                <option value="">Select Product</option>
                {ALL_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}

          {/* Pre-selected product display */}
          {!showProductSelect && (
            <div className="bg-[var(--text-primary)]/5 rounded px-4 py-3">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Selected Product</p>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{preSelectedProduct}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Color */}
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Color *</label>
              <select name="color" required value={form.color} onChange={handleChange}
                className="input-field w-full text-base sm:text-sm" data-testid="bulk-color-select">
                <option value="">Select Color</option>
                {colorOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Length */}
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Length *</label>
              <select name="length" required value={form.length} onChange={handleChange}
                className="input-field w-full text-base sm:text-sm" data-testid="bulk-length-select">
                <option value="">Select Length</option>
                {LENGTHS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Quantity (units) *</label>
            <input name="quantity" type="number" min="1" required value={form.quantity} onChange={handleChange}
              className="input-field w-full text-base sm:text-sm" placeholder="Minimum 10 units recommended" data-testid="bulk-quantity-input" />
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Additional Notes</label>
            <textarea name="additional_notes" rows={3} value={form.additional_notes} onChange={handleChange}
              className="input-field w-full resize-none text-base sm:text-sm" placeholder="Special requirements, custom color mixing, packaging preferences..."
              data-testid="bulk-notes-input" />
          </div>

          {/* Divider */}
          <div className="pt-2 pb-1">
            <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: 'var(--gold)' }}>Your Details</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Full Name *</label>
              <input name="customer_name" type="text" required value={form.customer_name} onChange={handleChange}
                className="input-field w-full text-base sm:text-sm" data-testid="bulk-name-input" />
            </div>
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Email *</label>
              <input name="customer_email" type="email" required value={form.customer_email} onChange={handleChange}
                className="input-field w-full text-base sm:text-sm" data-testid="bulk-email-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Phone *</label>
              <input name="customer_phone" type="tel" required value={form.customer_phone} onChange={handleChange}
                className="input-field w-full text-base sm:text-sm" placeholder="+1 (555) 123-4567" data-testid="bulk-phone-input" />
            </div>
            <div>
              <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Country *</label>
              <Select value={form.country} onValueChange={(val) => setForm(prev => ({...prev, country: val}))}>
                <SelectTrigger className="input-field w-full text-base sm:text-sm h-10" data-testid="bulk-country-select">
                  <SelectValue placeholder="Select Country" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px] z-[100]">
                  {Object.entries(COUNTRIES).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm sm:text-xs font-medium mb-1.5 text-[var(--text-secondary)]">Postal Code *</label>
            <input name="postal_code" type="text" required value={form.postal_code} onChange={handleChange}
              className="input-field w-full text-base sm:text-sm" data-testid="bulk-postal-input" />
          </div>

          {/* Submit */}
          <button type="submit" disabled={submitting}
            className="btn-primary w-full flex items-center justify-center gap-2 mt-2"
            data-testid="bulk-submit-btn">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Bulk Inquiry</>}
          </button>

          <p className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>
            Our team will respond within 24-48 hours with custom pricing
          </p>
        </form>
      </div>
    </div>
  );
};

export default BulkOrderForm;
